/**
 * Graph lights view: every tunable of the layout, the light model and the renderer in one place
 * (Documentation/specs/2026-11-03-graph-lights-view-plan.md sections 3, 4, 7 and 12). Colours live in lightsTheme.js.
 */

export const ROLES = Object.freeze({ CAUSE: "cause", SYMPTOM: "symptom", FACT: "fact" });

/** category_bundle graph node `type` -> light role. Node types not listed (none today) are not drawn. */
export const ROLE_OF_TYPE = Object.freeze({ pattern: ROLES.CAUSE, symptom: ROLES.SYMPTOM, fact: ROLES.FACT });

export const EDGE_KINDS = Object.freeze({ SUPPORTS: "supports", RULES_OUT: "rules_out" });

/** How a light is drawn; each tone has its own colour (lightsTheme.toneRgb) and glow sprite. */
export const TONES = Object.freeze({
  DIM: "dim",              // in the category, not part of the diagnosis (yet)
  LIT: "lit",              // a live candidate cause, an observed "yes"
  LEADING: "leading",      // leading or confirmed cause
  EVALUATING: "evaluating",// the cause being checked right now: pulses
  EMBER: "ember",          // ruled out or failed: kept faint so the elimination stays visible
  DENIED: "denied",        // an observed "no"
  FADED: "faded",          // a fact whose every supported cause is ruled out
});

export const STAGES = Object.freeze({
  NONE: "none", CATEGORY: "category", CANDIDATES: "candidates", NARROWING: "narrowing", LIKELY: "likely", ENDED: "ended",
});

/** diag_state cause.status values (tse_diag_state.schema.json). */
export const CAUSE_STATUS = Object.freeze({
  LEADING: "leading", LIVE: "live", EVALUATING: "evaluating", RULED_OUT: "ruled_out", CONFIRMED: "confirmed", FAILED: "failed",
});
export const OBSERVED_VALUE = Object.freeze({ YES: "yes", NO: "no", UNKNOWN: "unknown" });
export const LIKELY_PHASES = Object.freeze(["LIKELY_CAUSE", "HANDOFF"]);
export const ENDED_LIFECYCLE = "ended";

/** View options the header controls set (GraphLightsView state, read by the renderer every frame). */
export const EDGE_MODES = Object.freeze({ ALL: "all", TRAVERSED: "traversed", NONE: "none" });
export const THEME_MODES = Object.freeze({ NIGHT: "night", DAY: "day" });
export const DEFAULT_OPTIONS = Object.freeze({ edgeMode: EDGE_MODES.ALL, labels: true, facts: true, embers: true });

/** Base light per role: what the category looks like before (and outside of) the diagnosis. */
export const ROLE_STYLE = Object.freeze({
  [ROLES.CAUSE]: { radius: 2.8, litRadius: 6.8, baseAlpha: 0.3 },
  [ROLES.SYMPTOM]: { radius: 2, litRadius: 4.4, baseAlpha: 0.26 },
  [ROLES.FACT]: { radius: 1.4, litRadius: 3.2, baseAlpha: 0.18 },
});

/** Tones drawn at a fixed alpha and a shrunk radius instead of base + lit (they must read fainter than an untouched dim dot). */
export const TONE_FIXED_ALPHA = Object.freeze({ ember: 0.2, faded: 0.08 });
export const SHRUNK_RADIUS_SCALE = 0.8;
export const LEADING_HALO_SCALE = 2.6;
export const ZOOM_RADIUS_MAX = 3;        // dots grow with sqrt(zoom over fit), up to this factor

/** Light (0..1) per tone where the tone, not a percentage, decides it. */
export const TONE_LIT = Object.freeze({
  [TONES.LEADING]: 1, [TONES.EMBER]: 0.1, [TONES.DENIED]: 0.45, [TONES.FADED]: 0.05, [TONES.DIM]: 0,
});
/** Observed "yes" symptom / fact light, and an observed "unknown". */
export const OBSERVED_LIT = Object.freeze({ yes: 1, unknown: 0.3 });
/** A cause's light is sqrt(percentage / 100): a 4 % candidate still reads as lit, 60 % as bright. */
export const LIT_EXPONENT = 0.5;
export const LIT_MIN_CANDIDATE = 0.08;   // causes below this are not part of the camera focus

/** Edges: every bundle edge is drawn faint; a traversed one (both ends lit) is drawn in colour. */
export const EDGE = Object.freeze({
  MIN_LIT: 0.2,            // a supports edge counts as traversed when both ends are at least this lit
  ALPHA_SCALE: 0.7,        // traversed alpha = min(lit) * this
  RULES_OUT_ALPHA: 0.6,
  MIN_WIDTH: 0.7,
  WIN_WIDTH: 1.5,          // extra width at wins/seen = 1
  LEAD_WIDTH: 0.6,         // extra width on edges into the leading cause
  FAINT_WIDTH: 0.9,
  SELECTED_ALPHA: 0.95,    // edges of the selected node, and the selected edge
  SELECTED_EXTRA_WIDTH: 1, // the selected edge is drawn this much wider
  HOVER_ALPHA: 0.75,
  HIT_PX: 7,               // click / hover tolerance around an edge, in CSS px, independent of its drawn width
  DASH: Object.freeze([4, 4]),
});

/** Selection: the picked node and its neighbours keep their light, the rest dims. */
export const SELECTION = Object.freeze({ DIM_SCALE: 0.22, RING_PX: 13, RING_WIDTH: 1.5, FLY_SCALE_OVER_FIT: 2.2 });

/** Layout (world units; the camera fits the result to the canvas, so only proportions matter). */
export const LAYOUT = Object.freeze({
  CAUSE_SPACING: 26,          // Fibonacci spiral step inside a cluster
  CLUSTER_GAP: 110,           // empty space between two cluster discs
  LEAF_JITTER: 20,            // facts / symptoms scatter around the barycentre of their causes
  LEAF_INSET: 0.86,           // leaves stay within this share of the cluster radius (nothing sits under the label)
  RING_MAX_CLUSTERS: 6,       // more clusters than this go on a grid
  RELAX_ITERATIONS: 24,       // collide-only de-overlap passes, fixed count so the layout is deterministic
  MIN_DIST: { cause: 16, symptom: 8, fact: 6 },
  MEMO_SIZE: 4,               // layouts kept per page (one per category bundle seen)
});
export const GENERAL_CLUSTER = "__general__";
export const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** Camera and motion. */
export const CAMERA = Object.freeze({
  FIT_PADDING: 0.15,          // 15 % around the focus box
  MAX_SCALE_OVER_FIT: 6,      // never zoom in more than 6x the whole-category fit
  MIN_SCALE_UNDER_FIT: 0.5,   // manual zoom-out floor, relative to the whole-category fit
  MIN_FOCUS_SPAN: 120,        // a focus of one node still frames some neighbourhood (world units)
  LERP: 0.12,                 // per-frame approach toward the target camera (at 30 fps)
  WHEEL_MIN_STEP: 0.04,
  WHEEL_GAIN: 0.0015,
  KEY_ZOOM: 1.25,
  KEY_PAN_PX: 60,
  SETTLE_EPS: 0.002,
});
export const LIGHT_LERP = 0.18;          // per-frame brightness approach (about 450 ms to settle at 30 fps)
export const LIGHT_SETTLE_EPS = 0.004;
export const PULSE_PERIOD_MS = 1600;
export const PULSE_DEPTH = 0.35;
export const FRAME_INTERVAL_MS = 1000 / 30;
export const MAX_DPR = 2;

/** Glow sprites: one offscreen canvas per colour, drawn scaled. */
export const SPRITE = Object.freeze({ SIZE: 64, GLOW_SCALE: 4.2 });

/** Labels: none when zoomed out; then lit causes, then observed symptoms (screen px collision skip). */
export const LABELS = Object.freeze({
  CAUSE_MIN_SCALE_OVER_FIT: 1.5, SYMPTOM_MIN_SCALE_OVER_FIT: 2.5, CAUSE_MIN_LIT: 0.3, MAX: 24,
  FONT: "11px 'Space Grotesk', Inter, system-ui, sans-serif", CLUSTER_FONT: "600 12px 'Space Grotesk', Inter, system-ui, sans-serif",
  MAX_CHARS: 42, OFFSET_PX: 8, PAD_PX: 3, LINE_PX: 14, CLUSTER_MAX_SCALE_OVER_FIT: 2.2, CLUSTER_PILL_PAD_PX: 7, CLUSTER_PILL_H_PX: 20,
});

export const HOVER_RADIUS_PX = 10;
export const MAX_LIVE_CAUSES = 8;        // causes listed in the side panel and the aria-live mirror
export const SEARCH_MAX_RESULTS = 12;
export const CAUSE_KEY_PREFIX = "PC_";
